import { useMemo } from 'react';

import { type VehicleListing } from '@/services/vehicles/types';
import {
  countryName,
  formatEngine,
  formatMileage,
  fuelLabels,
  transmissionLabels,
  vehiclePath,
  vehicleTitle,
} from '@/utils/vehicleFormat';
import { vehicleEnquiryMessage } from '@/utils/vehicleMessages';

export type VehicleCardModel = {
  title: string;
  path: string;
  /** Trim and export country, already joined for display. */
  subtitle: string;
  /** Mileage, engine, fuel and gearbox — whichever of them are recorded. */
  specs: string[];
  whatsappMessage: string;
  /** Sold cars keep their page but lose every action that implies a purchase. */
  isSold: boolean;
};

/**
 * Everything a listing card displays, derived once from the listing row.
 *
 * The card itself stays a layout: it reads these fields and arranges them.
 * Formatting rules — how a mileage reads, what the subtitle joins, which
 * specs are worth the one line the card gives them — live here, so the /cars
 * grid, the home page and the "similar cars" rail cannot drift apart.
 */
export function useVehicleCardModel(v: VehicleListing): VehicleCardModel {
  return useMemo(
    () => ({
      title: vehicleTitle(v),
      path: vehiclePath(v),
      subtitle: [v.trimLevel, `From ${countryName(v.sourceCountry)}`].filter(Boolean).join(' · '),
      specs: [
        formatMileage(v.mileageKm),
        formatEngine(v.engineCc),
        v.fuelType && fuelLabels[v.fuelType],
        v.transmission && transmissionLabels[v.transmission],
      ].filter(Boolean) as string[],
      whatsappMessage: vehicleEnquiryMessage(v),
      isSold: v.availability === 'sold',
    }),
    [v],
  );
}
